import { MaterialTableProps } from 'material-table';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import TableLayout from '../../../components/Layout/TableLayout';
import TransitionModal from '../../../components/Modal/TransitionModal';
import TableWithEdit from '../../../components/Tables/TableWithEdit';
import { tKey } from '../../../helpers/translate';
import { showSnackbar } from '../../../redux/FeedbackAPI/actions';
import { SeveritySnackbar } from '../../../redux/FeedbackAPI/definitions';
import CenterCostSection from '../../../types/Accounting/CenterCost/CenterCostSection';
import CenterCostType from '../../../types/Accounting/CenterCost/CenterCostType';
import { CenterCostData } from '../../../types/Accounting/CenterCost/CenterCostTypes';
import AccountingSubmenu from '../AccountingSubmenu';
import useCenterCostList from './hooks/useCenterCostList';

interface CostCenterTableProps {
  centerCostSection: CenterCostSection;
  centerCostType: CenterCostType;
  showSnackbar: (message: string, severity: SeveritySnackbar, route: string | undefined, time: number) => void;
}

const CostCenterTable: React.FC<CostCenterTableProps> = ({ centerCostSection, centerCostType, showSnackbar }) => {
  const history = useHistory();

  const { centerCosts, errorListMessage, loading, removeCenterCost } = useCenterCostList(centerCostType);

  const [modal, setModal] = React.useState<{ id: number; show: boolean }>({ id: 0, show: false });

  useEffect(() => {
    if (errorListMessage) {
      showSnackbar(errorListMessage, 'error', undefined, 3000);
    }
  }, [errorListMessage, showSnackbar]);

  const columns = [
    {
      field: 'code',
      title: tKey('Código'),
    },
    {
      field: 'name',
      title: tKey('Descripción'),
    },
  ];

  if (centerCostType.isDelegation()) {
    columns.push({
      field: 'channel_code',
      title: tKey(CenterCostSection.canal().translationKey()),
    });
  }

  if (centerCostType.isArea()) {
    columns.push({
      field: 'delegation_code',
      title: tKey(CenterCostSection.delegacion().translationKey()),
    });
  }

  if (centerCostType.isProject()) {
    columns.push({
      field: 'area_code',
      title: tKey(CenterCostSection.departamento().translationKey()),
    });
  }

  if (centerCostType.isChannel() || centerCostType.isDelegation()) {
    columns.push({
      field: 'role_name',
      title: tKey('center_cost.role_code'),
    });
  }

  const tableData: MaterialTableProps<CenterCostData> = {
    title: tKey(centerCostSection.translationKey()),
    columns,
    data: centerCosts,
    isLoading: loading,
    options: {
      pageSize: 100,
      pageSizeOptions: [20, 50, 100],
    },
  };
  const onEditOne = (event: React.MouseEvent, rowData: CenterCostData | CenterCostData[]) => {
    if (!Array.isArray(rowData) && rowData) {
      history.push(`/contabilidad/centro-coste/${centerCostSection.value()}/editar/${rowData.id}`);
    }
  };

  const idItem = (id: number) => {
    setModal({ id, show: true });
  };

  const goToNew = () => {
    history.push(`/contabilidad/centro-coste/${centerCostSection.value()}/crear/0`);
  };

  const onRemoveOne = async (id: number) => {
    removeCenterCost(id);
  };

  return (
    <TableLayout
      leftSubmenu={<AccountingSubmenu selected={centerCostSection.value()} submenu={tKey('Centro de coste')} />}
    >
      <TableWithEdit
        tableData={tableData}
        key={JSON.stringify(tableData.data)}
        onEditOne={(event: React.MouseEvent, rowData: CenterCostData | CenterCostData[]) => onEditOne(event, rowData)}
        deleteFunc={(rowData: CenterCostData) => idItem(rowData.id)}
        optionsNew={{
          newButtonText: tKey('Nuevo'),
          onNew: goToNew,
        }}
        permission={true}
      />
      <TransitionModal
        view={modal.show}
        handleClose={() => {
          setModal({ id: 0, show: false });
        }}
        bodyModal={tKey('¿Seguro que desea eliminar este campo?')}
        buttonOk={tKey('Sí')}
        buttonKo={tKey('No')}
        handleYes={() => {
          onRemoveOne(modal.id);
          setModal({ id: 0, show: false });
        }}
      />
    </TableLayout>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  showSnackbar: (message: string, severity: SeveritySnackbar, route: string | undefined, time: number): void =>
    dispatch(showSnackbar(message, severity, route, time)),
});

export default connect(undefined, mapDispatchToProps)(CostCenterTable);
